/// <reference types="vite/client" />

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  skipTokenCheck?: boolean;
}

export type NavigationFunction = (path: string, options?: { replace?: boolean }) => void;

interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  error?: string;
  code?: string;
  details?: unknown;
}

export class HttpClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'HttpClientError';
  }

  // Helper method to check if this is an authentication error
  isAuthenticationError(): boolean {
    return this.status === 401 || 
           this.message.includes('Authentication') ||
           this.message.includes('Session expired') ||
           this.message.includes('Session duration exceeded') ||
           this.message.includes('Token') ||
           this.code === 'TOKEN_EXPIRED' ||
           this.code === 'SESSION_EXPIRED' ||
           this.code === 'ACCOUNT_DISABLED' ||
           this.code === 'ACCOUNT_EXPIRED';
  }

  // Helper method to check if this is a timeout error
  isTimeoutError(): boolean {
    return this.code === 'REQUEST_TIMEOUT' || this.message.includes('timeout');
  }
}

export class HttpClientTimeoutError extends HttpClientError {
  constructor(message: string = 'Request timeout') {
    super(message, 408, 'REQUEST_TIMEOUT');
    this.name = 'HttpClientTimeoutError';
  }
}

// Type guard for HttpClientError that works across bundle boundaries and with serialized errors
export function isHttpClientError(error: unknown): error is HttpClientError {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error instanceof HttpClientError ||
     (error as any)?.name === 'HttpClientError' ||
     (typeof (error as any)?.status === 'number' &&
      typeof (error as any)?.message === 'string' &&
      'name' in (error as any)))
  );
}

export class HttpClient {
  private baseURL: string;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private navigate: NavigationFunction | undefined;
  
  // Configurable timeout for refresh requests (in milliseconds)
  private readonly REFRESH_TIMEOUT_MS = 5000; // 5 seconds

  constructor(navigate?: NavigationFunction) {
    // Validate and assign API URL from environment
    const apiUrl = import.meta.env['VITE_API_URL'];
    
    if (!apiUrl || typeof apiUrl !== 'string' || apiUrl.trim() === '') {
      console.warn('⚠️ VITE_API_URL environment variable is not set or empty, using fallback');
      this.baseURL = '/api';
    } else {
      this.baseURL = apiUrl.trim();
    }
    
    this.navigate = navigate;
  }

  /**
   * Set the navigation function for SPA routing
   */
  setNavigationFunction(navigate?: NavigationFunction): void {
    this.navigate = navigate;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  private normalizeBase64(base64String: string): string {
    // Normalize URL-safe base64 to standard base64
    let normalized = base64String.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed (base64 strings must be divisible by 4)
    const padding = normalized.length % 4;
    if (padding) {
      normalized += '='.repeat(4 - padding);
    }
    
    return normalized;
  }

  private isTokenExpired(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return true;

    try {
      // Basic JWT structure check
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      // Decode payload safely
      const payloadPart = parts[1];
      if (!payloadPart) return true;
      
      // Normalize URL-safe base64 before decoding
      const normalizedPayload = this.normalizeBase64(payloadPart);
      const payload = JSON.parse(atob(normalizedPayload));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Check if token expires within the next 2 minutes (proactive refresh)
      return payload.exp && payload.exp - currentTime < 120;
    } catch {
      return true;
    }
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    if (this.isRefreshing) {
      // If already refreshing, wait for the existing refresh to complete
      if (this.refreshPromise) {
        return await this.refreshPromise;
      }
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } catch (error) {
      // Handle timeout errors specifically
      if (error instanceof HttpClientTimeoutError) {
        console.warn('🔴 HttpClient: Token refresh timed out, treating as auth failure');
        return false; // Return false so callers can retry or fail fast
      }
      
      // Re-throw other errors
      throw error;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async _performTokenRefresh(): Promise<boolean> {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    // Create AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, this.REFRESH_TIMEOUT_MS);

    try {
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('🔄 HttpClient: Attempting token refresh');
      }
      
      const response = await fetch(`${this.baseURL}/api/auth/refresh-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle different error scenarios
        if (errorData.requiresReauth || 
            errorData.code === 'ACCOUNT_DISABLED' || 
            errorData.code === 'ACCOUNT_EXPIRED' ||
            errorData.code === 'SESSION_EXPIRED') {
          console.warn('🔴 HttpClient: Session expired or account issue:', errorData.error);
          this.handleAuthFailure('Session expired. Please log in again.');
          return false;
        }
        
        console.warn('🔴 HttpClient: Token refresh failed:', errorData.error);
        return false;
      }

      const authData = await response.json();
      
      if (authData.success && authData.data?.token && authData.data?.user) {
        // Update stored token and user data
        localStorage.setItem('authToken', authData.data.token);
        localStorage.setItem('authUser', JSON.stringify(authData.data.user));
        
        if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
          console.debug('✅ HttpClient: Token refreshed successfully');
        }
        
        // Notify auth context of the update
        window.dispatchEvent(new CustomEvent('tokenRefreshed', {
          detail: { user: authData.data.user, token: authData.data.token }
        }));
        
        return true;
      } else {
        console.warn('🔴 HttpClient: Invalid refresh response format');
        return false;
      }

    } catch (error) {
      // Handle AbortError (timeout) specifically
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn('🔴 HttpClient: Token refresh timeout after', this.REFRESH_TIMEOUT_MS, 'ms');
        throw new HttpClientTimeoutError('Token refresh request timed out');
      }
      
      console.warn('🔴 HttpClient: Token refresh network error:', error);
      return false;
    } finally {
      // Always clear timeout to prevent memory leaks
      clearTimeout(timeoutId);
    }
  }

  private handleAuthFailure(message?: string): void {
    console.warn('🔴 HttpClient: Authentication failure, logging out');
    
    // Clear auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    
    // Preserve current location for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    const isSessionExpired = message?.includes('Session duration exceeded') ||
                            message?.includes('session has expired') ||
                            message?.includes('SESSION_EXPIRED');
    
    // Set session expired message
    if (message) {
      sessionStorage.setItem('sessionExpiredMessage', message);
    }
    
    // Notify auth context
    window.dispatchEvent(new CustomEvent('authFailure', { detail: { message } }));
    
    // Navigate to login if not already there and not on profile page (to prevent loops)
    const shouldNavigate = currentPath !== '/login' && 
                          !currentPath.startsWith('/login?') &&
                          currentPath !== '/profile' && 
                          currentPath !== '/profile/edit';
    
    if (shouldNavigate) {
      let loginUrl = '/login';
      
      // Add session expiration flag and return URL for session expiration
      if (isSessionExpired) {
        loginUrl = `/login?sessionExpired=true&returnUrl=${encodeURIComponent(currentPath)}`;
      }
      
      if (this.navigate) {
        // Use SPA navigation if available
        this.navigate(loginUrl, { replace: true });
      } else {
        // Fallback to hard redirect if navigation function not provided
        console.warn('🟡 HttpClient: No navigation function provided, falling back to hard redirect');
        window.location.href = loginUrl;
      }
    } else {
      console.warn('🔴 HttpClient: Auth failure detected but not redirecting to prevent loop', {
        currentPath,
        message
      });
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData: Partial<ApiResponse> = {};
      
      try {
        errorData = await response.json();
      } catch {
        // If response isn't JSON, use default error
      }

      // Handle authentication errors
      if (response.status === 401) {
        const errorMessage = errorData.error || 'Authentication failed';
        console.warn('🔴 HttpClient: 401 Unauthorized received:', {
          message: errorMessage,
          code: errorData.code,
          url: response.url,
          hasToken: !!localStorage.getItem('authToken')
        });
        
        // Check for specific error codes that require different handling
        if (errorData.code === 'REGISTRATION_INCOMPLETE') {
          // Don't logout for incomplete registration
          throw new HttpClientError(
            errorMessage,
            response.status,
            errorData.code,
            errorData.details
          );
        }
        
        if (errorData.code === 'TOKEN_EXPIRED_REFRESH_AVAILABLE' || 
            errorData.code === 'REQUIRES_REFRESH') {
          // Token expired but session is still valid - trigger refresh
          console.log('🔄 HttpClient: Token expired, attempting refresh');
          throw new HttpClientError(
            'Token refresh required',
            response.status,
            'TOKEN_REFRESH_REQUIRED',
            errorData.details
          );
        }
        
        // For other 401 errors, trigger logout
        this.handleAuthFailure(errorMessage);
        throw new HttpClientError(errorMessage, response.status, errorData.code, errorData.details);
      }

      // Handle other HTTP errors
      throw new HttpClientError(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData.code,
        errorData.details
      );
    }

    const jsonResponse = await response.json() as ApiResponse<T>;
    
    // Extract the data from the API response structure
    if (jsonResponse.success) {
      // For successful responses, return data if present, otherwise return the response itself
      // This handles DELETE responses that don't have a data field
      return jsonResponse.data !== undefined ? jsonResponse.data : (jsonResponse as T);
    } else {
      // Handle API-level errors
      throw new HttpClientError(
        jsonResponse.error || 'API request failed',
        response.status,
        jsonResponse.code,
        jsonResponse.details
      );
    }
  }

  private shouldStringifyBody(data: unknown): boolean {
    // Don't stringify these types - pass them through as-is
    if (data instanceof FormData ||
        data instanceof URLSearchParams ||
        data instanceof Blob ||
        data instanceof ArrayBuffer ||
        typeof data === 'string') {
      return false;
    }
    
    // For plain objects and arrays, stringify them
    return data !== null && data !== undefined;
  }

  private prepareRequestBody(data: unknown): BodyInit | null {
    // Only log in development and staging
    if (import.meta.env['VITE_APP_ENV'] !== 'production') {
      console.log('HttpClient.prepareRequestBody - input data:', data);
      console.log('HttpClient.prepareRequestBody - input type:', typeof data);
    }
    
    if (data === null || data === undefined) {
      if (import.meta.env['VITE_APP_ENV'] !== 'production') {
        console.log('HttpClient.prepareRequestBody - returning null (data is null/undefined)');
      }
      return null;
    }
    
    if (this.shouldStringifyBody(data)) {
      const stringified = JSON.stringify(data);
      if (import.meta.env['VITE_APP_ENV'] !== 'production') {
        console.log('HttpClient.prepareRequestBody - stringified:', stringified);
      }
      return stringified;
    }
    
    if (import.meta.env['VITE_APP_ENV'] !== 'production') {
      console.log('HttpClient.prepareRequestBody - passing through as-is');
    }
    // Pass through FormData, URLSearchParams, Blob, ArrayBuffer, string as-is
    return data as BodyInit;
  }

  async request<T = unknown>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { skipAuth = false, skipTokenCheck = false, ...fetchConfig } = config;
    
    let url = endpoint;
    if (!endpoint.startsWith('http')) {
      url = `${this.baseURL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    }

    // Prepare headers - only set Content-Type for JSON bodies
    const headers: HeadersInit = {
      ...fetchConfig.headers,
    };

    // Only set Content-Type for JSON bodies, let browser handle FormData boundary
    if (fetchConfig.body) {
      // If body is a string and looks like JSON, set Content-Type
      if (typeof fetchConfig.body === 'string') {
        try {
          JSON.parse(fetchConfig.body);
          (headers as Record<string, string>)['Content-Type'] = 'application/json';
        } catch {
          // Not JSON, don't set Content-Type
        }
      } else if (this.shouldStringifyBody(fetchConfig.body)) {
        (headers as Record<string, string>)['Content-Type'] = 'application/json';
      }
    }

    // Add auth headers if not skipped
    if (!skipAuth) {
      Object.assign(headers, this.getAuthHeaders());
      
      // Check for token expiry and attempt refresh if needed (unless skipped)
      if (!skipTokenCheck && this.isTokenExpired()) {
        if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
          console.debug('🔄 HttpClient: Token is expired/expiring soon, attempting refresh');
        }
        const refreshSuccess = await this.attemptTokenRefresh();
        if (refreshSuccess) {
          // Add the new token to headers
          Object.assign(headers, this.getAuthHeaders());
        }
      }
    }

    const requestConfig: RequestInit = {
      ...fetchConfig,
      headers,
    };

    // Enhanced logging for debugging (not just development)
    const shouldLog = import.meta.env['VITE_APP_ENV'] !== 'production' || 
                     import.meta.env['VITE_APP_ENV'] === 'staging';
    
    if (shouldLog) {
      console.log('🔍 HttpClient.request - final requestConfig:', {
        method: requestConfig.method,
        body: requestConfig.body,
        bodyType: typeof requestConfig.body,
        headers: {
          ...requestConfig.headers,
          // Redact sensitive headers but show if they exist
          'Authorization': (requestConfig.headers as any)?.['Authorization'] ? 'Bearer [REDACTED]' : 'none'
        },
        url,
        timestamp: new Date().toISOString()
      });
    }

    try {
      const response = await fetch(url, requestConfig);
      
      // Log response details for debugging
      if (shouldLog) {
        console.log('🔍 HttpClient.request - response received:', {
          url,
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          timestamp: new Date().toISOString()
        });
      }
      
      return await this.handleResponse<T>(response);
    } catch (error) {
      // Log fetch errors
      if (shouldLog) {
        console.error('🚨 HttpClient.request - fetch error:', {
          url,
          error: error instanceof Error ? {
            name: error.name,
            message: error.message,
            stack: error.stack
          } : error,
          timestamp: new Date().toISOString()
        });
      }
      if (error instanceof HttpClientError && error.code === 'TOKEN_REFRESH_REQUIRED') {
        // Token expired during request - attempt refresh and retry once
        if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
          console.debug('🔄 HttpClient: Retrying request after token refresh');
        }
        
        const refreshSuccess = await this.attemptTokenRefresh();
        if (refreshSuccess && !skipAuth) {
          // Update headers with new token and retry
          const updatedHeaders = { ...headers, ...this.getAuthHeaders() };
          const retryConfig: RequestInit = {
            ...requestConfig,
            headers: updatedHeaders,
          };
          
          const retryResponse = await fetch(url, retryConfig);
          return await this.handleResponse<T>(retryResponse);
        } else {
          // Refresh failed, handle as auth failure
          this.handleAuthFailure('Session expired. Please log in again.');
          throw error;
        }
      }
      
      throw error;
    }
  }

  // Convenience methods
  async get<T = unknown>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T = unknown>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: this.prepareRequestBody(data),
    });
  }

  async patch<T = unknown>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: this.prepareRequestBody(data),
    });
  }

  async put<T = unknown>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: this.prepareRequestBody(data),
    });
  }

  async delete<T = unknown>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

// Create a default instance without navigation (for backward compatibility)
export const httpClient = new HttpClient();

// Factory function to create HttpClient with navigation
export function createHttpClient(navigate?: NavigationFunction): HttpClient {
  return new HttpClient(navigate);
}

export default httpClient;
